// src/pages/ChannelSubscription.tsx
import React, { useState, useEffect, useContext, ChangeEvent } from 'react';
import {
  Container, Typography, Grid, Box, Paper, Chip, Alert, CircularProgress,
  FormControl, Select, MenuItem, TextField, Button, Checkbox, FormControlLabel,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, IconButton,
  Collapse, Divider, Tooltip, InputLabel, Slider
} from '@mui/material';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';
import { useNavigate } from 'react-router-dom';
import { getUserFriendlyError } from '../utils/errorHandler';
import AccountPickerMulti from '../components/AccountPickerMulti';
import type { AccountGroup } from '../components/AccountPickerMulti';
import { useFieldValidation } from '../hooks/useFieldValidation';
import type { FieldLimit, PairRule, CrossRule } from '../hooks/useFieldValidation';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';


// ==== Границы полей: 1-в-1 из StartChannelSubscriptionCommand (pydantic ge/le) ====
const CS_LIMITS: Record<string, FieldLimit> = {
  // total_subscriptions_limit: Optional[int], ge=1
  totalSubscriptionsLimit: { min: 1, integer: true, optional: true, helper: '0 или пусто = без лимита' },
  // subscriptions_per_account: Optional[int], ge=0
  subscriptionsPerAccount: { min: 0, integer: true, optional: true, helper: 'Макс на каждый аккаунт. 0 или пусто = без лимита' },
  // daily_limit_per_account: int, ge=1 le=200
  dailyLimitPerAccount: { min: 1, max: 200, integer: true, helper: 'По умолчанию: 50' },
  // time_limit_hours: Optional[float], ge=0.1 le=72.0
  timeLimitHours: { min: 0.1, max: 72, optional: true, helper: '0 или пусто — без лимита' },
  // min_delay: int, ge=0 le=300
  minDelay: { min: 0, max: 300, integer: true, helper: 'По умолчанию: 5' },
  // max_delay: int, ge=0 le=600
  maxDelay: { min: 0, max: 600, integer: true, helper: 'По умолчанию: 15' },
  // subscriptions_per_hour: Optional[int], ge=1 le=200
  subscriptionsPerHour: { min: 1, max: 200, integer: true, optional: true, helper: '0 или пусто = без лимита. По умолчанию: 30' },
  // hourly_pause: Optional[int], ge=60 le=7200
  hourlyPause: { min: 60, max: 7200, integer: true, optional: true, helper: '0 или пусто = без пауз' },
  // account_switch_delay: int, ge=0 le=300
  accountSwitchDelay: { min: 0, max: 300, integer: true, helper: 'По умолчанию: 30' },
  // read_description_delay_min: int, ge=0 le=60
  readDescriptionDelayMin: { min: 0, max: 60, integer: true, helper: 'По умолчанию: 3' },
  // read_description_delay_max: int, ge=0 le=120
  readDescriptionDelayMax: { min: 0, max: 120, integer: true, helper: 'По умолчанию: 10' },
  // max_retries: int, ge=0 le=10
  maxRetries: { min: 0, max: 10, integer: true, helper: 'По умолчанию: 3' },
  // max_parallel_accounts: int, ge=1 le=25
  maxParallelAccounts: { min: 1, max: 25, integer: true, helper: 'По умолчанию: 1' },
  // save_interval: int, ge=1 le=100
  saveInterval: { min: 1, max: 100, integer: true, helper: 'По умолчанию: 1' },
  // activity_mode_hours: float, ge=0
  activityModeHours: { min: 0, step: 0.5, optional: true, helper: '0 = выключено' },
  // activity_posts_view_count: int, ge=1 le=20
  activityPostsViewCount: { min: 1, max: 20, integer: true },
  // activity_iter_pause_min: float, ge=5
  activityIterPauseMin: { min: 5 },
  // activity_iter_pause_max: float, ge=10
  activityIterPauseMax: { min: 10 },
};

const CS_PAIRS: PairRule[] = [
  { minKey: 'minDelay', maxKey: 'maxDelay' },
  { minKey: 'readDescriptionDelayMin', maxKey: 'readDescriptionDelayMax' },
  { minKey: 'activityIterPauseMin', maxKey: 'activityIterPauseMax' },
];

const CS_CROSS: CrossRule[] = [
  // total_subscriptions_limit >= subscriptions_per_account (если оба заданы)
  {
    targetKey: 'totalSubscriptionsLimit',
    validate: (v) => {
      const total = v.totalSubscriptionsLimit?.trim();
      const per = v.subscriptionsPerAccount?.trim();
      if (!total || !per) return null;
      const t = Number(total.replace(',', '.'));
      const p = Number(per.replace(',', '.'));
      if (!Number.isNaN(t) && !Number.isNaN(p) && t < p) {
        return 'Общий лимит не может быть меньше лимита на аккаунт';
      }
      return null;
    },
  },
];


interface Account {
  id: string;
  name: string;
  phone_number: string;
}

interface ActiveSubscription {
  task_id: string;
  status: string;
  accounts_count: number;
  channels_count: number;
  channels_preview: string[];
  progress: { subscribed: number; failed: number; already_subscribed: number; skipped: number; total: number; percentage: number };
  started_at?: string;
  stopped_at?: string;
  duration?: number;
  status_message?: string;
}

const ChannelSubscription: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [activeSubscriptions, setActiveSubscriptions] = useState<ActiveSubscription[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [tab, setTab] = useState<'setup' | 'active'>('setup');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [form, setForm] = useState({
    accountNames: [] as string[],
    channelIdentifiers: '',
    totalSubscriptionsLimit: '',
    subscriptionsPerAccount: '',
    dailyLimitPerAccount: '50',
    timeLimitHours: '',
    minDelay: '5',
    maxDelay: '15',
    subscriptionsPerHour: '30',
    hourlyPause: '',
    accountSwitchDelay: '30',
    naturalBehavior: true,
    readDescriptionDelayMin: '3',
    readDescriptionDelayMax: '10',
    maxRetries: '3',
    skipOnLongFlood: true,
    continueOnError: true,
    randomizeOrder: true,
    useParallelAccounts: false,
    maxParallelAccounts: '1',
    distributionStrategy: 'equal' as 'equal' | 'random' | 'sequential',
    saveInterval: '1',
    // Режим активности
    activityModeHours: '',
    activityPostsViewCount: '5',
    activityReactionChance: '0.3',
    activityIterPauseMin: '120',
    activityIterPauseMax: '600',
  });

  const [channelsFile, setChannelsFile] = useState<File | null>(null);

  const update = (key: keyof typeof form, value: any) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const validation = useFieldValidation({ limits: CS_LIMITS, pairs: CS_PAIRS, cross: CS_CROSS });

  const handleChannelsFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['csv', 'xlsx', 'xls', 'json'].includes(ext || '')) {
      setMessage({ text: 'Файл должен быть .csv, .xlsx, .xls или .json', type: 'error' });
      return;
    }
    setChannelsFile(file);
    setForm(prev => ({ ...prev, channelIdentifiers: '' }));
    setMessage({ text: `Файл каналов загружен: ${file.name}`, type: 'info' });
  };

  useEffect(() => {
    if (!auth?.isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
    const interval = setInterval(fetchActive, 5000);
    return () => clearInterval(interval);
  }, [auth?.isAuthenticated, navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [accRes, subsRes] = await Promise.all([
        api.get('/accounts'),
        api.get('/subscribe_to_channels/list')
      ]);
      api.get('/account_groups').then(r => setGroups(r.data || [])).catch(() => {});
      setAccounts(accRes.data.accounts || []);
      setActiveSubscriptions(subsRes.data || []);
    } catch (err: any) {
      const text = getUserFriendlyError(err, accounts, 'загрузку');
      setMessage({ text, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActive = async () => {
    try {
      const res = await api.get('/subscribe_to_channels/list');
      setActiveSubscriptions(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const startSubscription = async () => {
  if (!form.accountNames.length || (!channelsFile && !form.channelIdentifiers.trim())) {
    setMessage({ text: 'Выберите аккаунты и укажите каналы', type: 'error' });
    return;
  }

  if (!validation.validateAll(form)) {
    setMessage({ text: 'Проверьте выделенные поля — есть ошибки валидации', type: 'error' });
    return;
  }

  const formData = new FormData();

  // Аккаунты — правильно
  form.accountNames.forEach(name => formData.append('account_names', name));

  // Каналы — правильно
  if (!channelsFile && form.channelIdentifiers.trim()) {
    const channels = form.channelIdentifiers.split(',').map(s => s.trim()).filter(Boolean);
    channels.forEach(ch => formData.append('channel_identifiers', ch));
  }

  if (channelsFile) formData.append('channels_file', channelsFile);

  // УМНАЯ ОТПРАВКА: 0 и пустая строка = поле НЕ отправляется → на бэке будет None
  const appendIfSet = (key: string, value: string | number | boolean) => {
    if (value === '' || value === '0' || value === 0 || value === null || value === undefined) {
      return; // ← НЕ отправляем = None на бэкенде
    }
    formData.append(key, String(value));
  };

  appendIfSet('total_subscriptions_limit', form.totalSubscriptionsLimit);
  appendIfSet('subscriptions_per_account', form.subscriptionsPerAccount);
  appendIfSet('time_limit_hours', form.timeLimitHours);
  appendIfSet('subscriptions_per_hour', form.subscriptionsPerHour);
  appendIfSet('hourly_pause', form.hourlyPause);

  // Эти поля всегда отправляем (у них безопасные дефолты)
  formData.append('daily_limit_per_account', form.dailyLimitPerAccount);
  formData.append('min_delay', form.minDelay);
  formData.append('max_delay', form.maxDelay);
  formData.append('account_switch_delay', form.accountSwitchDelay);
  formData.append('natural_behavior', String(form.naturalBehavior));
  formData.append('read_description_delay_min', form.readDescriptionDelayMin);
  formData.append('read_description_delay_max', form.readDescriptionDelayMax);
  formData.append('max_retries', form.maxRetries);
  formData.append('skip_on_long_flood', String(form.skipOnLongFlood));
  formData.append('continue_on_error', String(form.continueOnError));
  formData.append('randomize_order', String(form.randomizeOrder));
  formData.append('use_parallel_accounts', String(form.useParallelAccounts));
  formData.append('max_parallel_accounts', form.maxParallelAccounts);
  formData.append('distribution_strategy', form.distributionStrategy);
  formData.append('save_interval', form.saveInterval);

  // Режим активности
  appendIfSet('activity_mode_hours', form.activityModeHours);
  formData.append('activity_posts_view_count', form.activityPostsViewCount);
  formData.append('activity_reaction_chance', form.activityReactionChance);
  formData.append('activity_iter_pause_min', form.activityIterPauseMin);
  formData.append('activity_iter_pause_max', form.activityIterPauseMax);

  setIsLoading(true);
  setUploadProgress(0);

  try {
    await api.post('/subscribe_to_channels/start', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        const percent = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
        setUploadProgress(percent);
      }
    });

    setMessage({ text: 'Автоподписка запущена!', type: 'success' });
    setTab('active');
    setForm(prev => ({ ...prev, channelIdentifiers: '' }));
    setChannelsFile(null);
  } catch (err: any) {
    const text = getUserFriendlyError(err, accounts, 'автоподписку');
    setMessage({ text, type: 'error' });
  } finally {
    setIsLoading(false);
    setUploadProgress(0);
  }
};

  const stopSubscription = async (taskId: string) => {
    setIsLoading(true);
    try {
      await api.get(`/subscribe_to_channels/stop/${taskId}`);
      setMessage({ text: 'Подписка остановлена', type: 'info' });
      fetchActive();
    } catch (err: any) {
      const text = getUserFriendlyError(err, accounts, 'остановку подписки');
      setMessage({ text, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const restoreSubscription = async (taskId: string) => {
    setIsLoading(true);
    try {
      await api.get(`/subscribe_to_channels/restore/${taskId}`);
      setMessage({ text: 'Подписка возобновлена!', type: 'success' });
      fetchActive();
    } catch (err: any) {
      const text = getUserFriendlyError(err, accounts, 'возобновление подписки');
      setMessage({ text, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSubscription = async (taskId: string) => {
    if (!window.confirm('Удалить автоподписку НАВСЕГДА?')) return;
    setIsLoading(true);
    try {
      await api.delete(`/subscribe_to_channels/remove/${taskId}`);
      setMessage({ text: 'Автоподписка удалена навсегда', type: 'info' });
      fetchActive();
    } catch (err: any) {
      const text = getUserFriendlyError(err, accounts);
      setMessage({ text, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const getTabStyle = (current: 'setup' | 'active') => ({
    flex: 1,
    py: 2,
    borderRadius: 0,
    bgcolor: tab === current ? '#E0F2FE' : 'transparent',
    color: tab === current ? '#1E40AF' : '#6B7280',
    fontWeight: tab === current ? 600 : 400,
    '&:hover': { bgcolor: tab === current ? '#DBEAFE' : '#F3F4F6' }
  });

  if (isLoading && accounts.length === 0) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#F8FAFC', borderRadius: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 2 }} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <GroupAddIcon sx={{ fontSize: 40, color: '#1E40AF' }} />
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A8A', mb: 1 }}>
              Автоподписка на каналы
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label="Накрутка подписок" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Совместимость с парсером каналов" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Имитация конверсии" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Естественное поведение" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
            </Box>
            <Typography sx={{ color: '#4B5563', fontSize: '0.95rem', lineHeight: 1.6 }}>
              Автоматическая подписка ваших аккаунтов на каналы для накрутки, улучшения статистики конверсии с рекламы, или продвижения проектов
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6 }}>
        <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3, fontWeight: 600 }}>
          Как работает автоподписка?
        </Typography>
        <Typography sx={{ color: '#4B5563', mb: 3, lineHeight: 1.7 }}>
          Ваши аккаунты автоматически подписываются на указанные каналы с задержками и лимитами, имитируя естественный прирост подписчиков.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Несколько режимов</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Различные паттерны подписки на каналы позволяют не ставить аккаунты под угрозу бана.
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Лимиты и задержки</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Дневные/часовые лимиты, рандомные паузы, растягивание по времени - сервисы статистики видят естественный прирост.
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Поддержка пригласительных ссылок</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Имитируйте подписку на каналы по переданной пригласительной ссылке - продемонстрируйте рекламодателям хорошую конверсию
            </Typography>
          </Box>
        </Box>

        {/*<Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>*/}
        {/*  <Box sx={{ flex: 1 }}>*/}
        {/*    <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Распределение</Typography>*/}
        {/*    <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>*/}
        {/*      Равномерно, случайно или последовательно по аккаунтам.*/}
        {/*    </Typography>*/}
        {/*  </Box>*/}
        {/*  <Box sx={{ flex: 1 }}>*/}
        {/*    <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Статистика</Typography>*/}
        {/*    <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>*/}
        {/*      Подписано, ошибки, прогресс — всё в реальном времени.*/}
        {/*    </Typography>*/}
        {/*  </Box>*/}
        {/*  <Box sx={{ flex: 1 }}>*/}
        {/*    <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Остановка в один клик</Typography>*/}
        {/*    <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>*/}
        {/*      В любой момент можно остановить, возобновить или удалить.*/}
        {/*    </Typography>*/}
        {/*  </Box>*/}
        {/*</Box>*/}

        <Typography sx={{ color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic' }}>
          Накрутка подписчиков, имитация конверсии, анализ аудитории — безопасно и эффективно.
        </Typography>
             <Typography sx={{color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic'}}>
          Ознакомьтесь с документацией по автоматизации{' '}
          <Typography
              component="a"
              href="https://typhon.gitbook.io/docs/automatizations_telegram/autosubscribe"
              sx={{textDecoration: 'underline', cursor: 'pointer'}}
          >
            здесь
          </Typography>
          </Typography>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex' }}>
          <Button onClick={() => setTab('setup')} sx={getTabStyle('setup')} startIcon={<PlayArrowIcon />}>
            Настройка
          </Button>
          <Button onClick={() => setTab('active')} sx={getTabStyle('active')} startIcon={<StopIcon />}>
            Мои процессы подписки ({activeSubscriptions.length})
          </Button>
        </Box>

        <Box sx={{ p: 4 }}>
          {tab === 'setup' && (
            <Grid container spacing={3}>
              <Grid size={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Аккаунты
                  <Tooltip title="Выберите аккаунты для подписки">
                    <InfoIcon fontSize="small" sx={{ ml: 1, color: '#94A3B8' }} />
                  </Tooltip>
                </Typography>
                <AccountPickerMulti
                  accounts={accounts}
                  selected={form.accountNames}
                  onChange={(names) => update('accountNames', names)}
                  groups={groups}
                />
              </Grid>

              <Grid size={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Каналы
                  <Tooltip title="Введите usernames, ссылки или ID через запятую, или загрузите файл">
                    <InfoIcon fontSize="small" sx={{ ml: 1, color: '#94A3B8' }} />
                  </Tooltip>
                </Typography>
                <TextField
                  label="Каналы через запятую"
                  value={form.channelIdentifiers}
                  onChange={e => update('channelIdentifiers', e.target.value)}
                  fullWidth
                  disabled={!!channelsFile}
                  placeholder="@channel1, https://t.me/channel2, 123456789"
                  helperText={channelsFile ? 'Файл загружен — ручной ввод отключён' : 'Usernames, ссылки или ID'}
                />

                <Box sx={{ mt: 2, p: 3, bgcolor: '#F8FAFC', borderRadius: 2, border: '2px dashed #CBD5E1' }}>
                  <Button variant="outlined" component="label" startIcon={<AttachFileIcon />}>
                    Загрузить файл каналов (.csv, .xlsx, .json)
                    <input type="file" hidden accept=".csv,.xlsx,.xls,.json" onChange={handleChannelsFile} />
                  </Button>

                  {channelsFile && (
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachFileIcon color="primary" />
                      <Typography variant="body2">{channelsFile.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({(channelsFile.size / 1024 / 1024).toFixed(2)} МБ)
                      </Typography>
                      <IconButton size="small" onClick={() => setChannelsFile(null)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    В файле колонка: username, link или id
                  </Typography>
                </Box>
              </Grid>

              <Grid size={6}>
                <TextField
                  label="Общий лимит подписок"
                  type="number"
                  {...validation.fieldProps('totalSubscriptionsLimit', form, update)}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Подписок на аккаунт"
                  type="number"
                  {...validation.fieldProps('subscriptionsPerAccount', form, update)}
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <TextField
                  label="Дневной лимит на аккаунт"
                  type="number"
                  {...validation.fieldProps('dailyLimitPerAccount', form, update)}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Временной лимит (часы)"
                  type="number"
                  {...validation.fieldProps('timeLimitHours', form, update)}
                  fullWidth
                />
              </Grid>

              {/* ── РЕЖИМ АКТИВНОСТИ ──────────────────────────────────────── */}
              <Grid size={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1E3A8A', mb: 0.5 }}>
                  Активность после подписки
                  <Tooltip title="После подписки на каждый канал аккаунт сразу просматривает последние посты и слушает новые. При получении нового поста — ждёт случайную задержку, затем открывает канал, смотрит посты и возможно ставит реакцию. Работает весь указанный период.">
                    <InfoIcon fontSize="small" sx={{ ml: 1, color: '#94A3B8', verticalAlign: 'middle' }} />
                  </Tooltip>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                   Если оставить пустым — аккаунты просто подпишутся и завершат работу.
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={12}>
                    <TextField
                      label="Часов активности после подписки"
                      type="number"
                      {...validation.fieldProps('activityModeHours', form, update)}
                      fullWidth
                    />
                  </Grid>

                  <Grid size={6}>
                    <TextField
                      label="Постов просматривать за визит"
                      type="number"
                      {...validation.fieldProps('activityPostsViewCount', form, update)}
                      fullWidth
                    />
                  </Grid>

                  <Grid size={6}>
                    <Typography variant="body2" gutterBottom sx={{ color: '#374151' }}>
                      Шанс реакции на пост:{' '}
                      <strong style={{ color: '#1E40AF' }}>
                        {Math.round(parseFloat(form.activityReactionChance || '0') * 100)}%
                      </strong>
                    </Typography>
                    <Slider
                      value={parseFloat(form.activityReactionChance || '0')}
                      onChange={(_, v) => update('activityReactionChance', String(v as number))}
                      min={0} max={1} step={0.05}
                      marks={[{ value: 0, label: '0%' }, { value: 0.5, label: '50%' }, { value: 1, label: '100%' }]}
                      sx={{ color: '#1E40AF' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Вероятность поставить реакцию при каждом визите в канал
                    </Typography>
                  </Grid>

                  {form.activityModeHours && parseFloat(form.activityModeHours) > 0 && (
                    <>
                      <Grid size={6}>
                        <TextField
                          label="Мин. задержка перед визитом (сек)"
                          type="number"
                          {...validation.fieldProps('activityIterPauseMin', form, update)}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={6}>
                        <TextField
                          label="Макс. задержка перед визитом (сек)"
                          type="number"
                          {...validation.fieldProps('activityIterPauseMax', form, update)}
                          fullWidth
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
              {/* ── КОНЕЦ РЕЖИМА АКТИВНОСТИ ───────────────────────────────── */}

              <Grid size={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ justifyContent: 'space-between', py: 1.5 }}
                >
                  Расширенные настройки
                </Button>
              </Grid>

              <Grid size={12}>
                <Collapse in={showAdvanced}>
                  <Box sx={{ pl: 2, pt: 2, borderLeft: '4px solid #DBEAFE', bgcolor: '#F8FAFC', borderRadius: 1, p: 3 }}>
                    <Grid container spacing={3}>
                      <Grid size={6}>
                        <TextField label="Мин. задержка (сек)" type="number" {...validation.fieldProps('minDelay', form, update)} fullWidth />
                      </Grid>
                      <Grid size={6}>
                        <TextField label="Макс. задержка (сек)" type="number" {...validation.fieldProps('maxDelay', form, update)} fullWidth />
                      </Grid>

                      <Grid size={6}>
                        <TextField label="Подписок в час" type="number" {...validation.fieldProps('subscriptionsPerHour', form, update)} fullWidth />
                      </Grid>
                      <Grid size={6}>
                        <TextField label="Пауза после часа (сек)" type="number" {...validation.fieldProps('hourlyPause', form, update)} fullWidth />
                      </Grid>

                      <Grid size={6}>
                        <TextField label="Задержка при смене акка (сек)" type="number" {...validation.fieldProps('accountSwitchDelay', form, update)} fullWidth />
                      </Grid>
                      <Grid size={6}>
                        <TextField label="Макс. попыток" type="number" {...validation.fieldProps('maxRetries', form, update)} fullWidth />
                      </Grid>

                      <Grid size={12}><Divider sx={{ my: 2 }} /></Grid>

                      <Grid size={12}>
                        <FormControlLabel
                          control={<Checkbox checked={form.naturalBehavior} onChange={e => update('naturalBehavior', e.target.checked)} />}
                          label="Естественное поведение"
                        />
                      </Grid>

                      <Grid size={6}>
                        <TextField label="Чтение описания мин (сек)" type="number" {...validation.fieldProps('readDescriptionDelayMin', form, update)} fullWidth />
                      </Grid>
                      <Grid size={6}>
                        <TextField label="Чтение описания макс (сек)" type="number" {...validation.fieldProps('readDescriptionDelayMax', form, update)} fullWidth />
                      </Grid>

                      <Grid size={12}>
                        <FormControlLabel
                          control={<Checkbox checked={form.skipOnLongFlood} onChange={e => update('skipOnLongFlood', e.target.checked)} />}
                          label="Пропускать при долгом FloodWait (>1 час)"
                        />
                      </Grid>
                      <Grid size={12}>
                        <FormControlLabel
                          control={<Checkbox checked={form.continueOnError} onChange={e => update('continueOnError', e.target.checked)} />}
                          label="Продолжать при ошибках"
                        />
                      </Grid>
                      <Grid size={12}>
                        <FormControlLabel
                          control={<Checkbox checked={form.randomizeOrder} onChange={e => update('randomizeOrder', e.target.checked)} />}
                          label="Перемешивать порядок каналов"
                        />
                      </Grid>

                      <Grid size={12}>
                        <FormControlLabel
                          control={<Checkbox checked={form.useParallelAccounts} onChange={e => update('useParallelAccounts', e.target.checked)} />}
                          label="Параллельная работа аккаунтов"
                        />
                      </Grid>
                      {form.useParallelAccounts && (
                        <Grid size={12}>
                          <TextField label="Макс. параллельных аккаунтов" type="number" {...validation.fieldProps('maxParallelAccounts', form, update)} fullWidth />
                        </Grid>
                      )}

                     <Grid size={12}>
  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#1E40AF' }}>
    Стратегия распределения подписок
    <Tooltip title="Порядок подписывания каналов на аккаунты">
      <InfoIcon fontSize="small" sx={{ ml: 1, color: '#94A3B8' }} />
    </Tooltip>
  </Typography>

  <FormControl fullWidth>
    <InputLabel id="distribution-strategy-label">Выберите стратегию</InputLabel>
    <Select
      labelId="distribution-strategy-label"
      value={form.distributionStrategy}
      onChange={e => update('distributionStrategy', e.target.value as 'equal' | 'random' | 'sequential')}
      label="Выберите стратегию"
    >
      <MenuItem value="equal">
        <Box>
          <strong>Равномерно</strong>
          <Typography variant="body2" color="text.secondary">
            Аккаунты равномерно между собой распределяют каналы для подписки в разной очередности
          </Typography>
        </Box>
      </MenuItem>

      <MenuItem value="random">
        <Box>
          <strong>Случайно</strong>
          <Typography variant="body2" color="text.secondary">
           Каждый аккаунт получает каналы в своём уникальном случайном порядке.
          </Typography>
        </Box>
      </MenuItem>

      <MenuItem value="sequential">
        <Box>
          <strong>Последовательно (по очереди)</strong>
          <Typography variant="body2" color="text.secondary">
             Все аккаунты проходят каналы в одном порядке сверху вниз.
          </Typography>
        </Box>
      </MenuItem>
    </Select>
  </FormControl>

  <Box sx={{ mt: 2, p: 3, bgcolor: '#F0FDF4', borderRadius: 2, border: '1px solid #BBF7D0' }}>
    <Typography variant="body2" color="success.main" fontWeight={500}>
      Рекомендуем «Равномерно» — самый безопасный и естественный режим
    </Typography>
  </Box>
</Grid>

                      <Grid size={12}>
                        <TextField label="Интервал сохранения (подписок)" type="number" {...validation.fieldProps('saveInterval', form, update)} fullWidth />
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Grid>

              <Grid size={12}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={startSubscription}
                  disabled={isLoading || !form.accountNames.length || (!channelsFile && !form.channelIdentifiers.trim()) || validation.hasErrors}
                  startIcon={isLoading ? <CircularProgress size={24} /> : <PlayArrowIcon />}
                  fullWidth
                  sx={{ py: 2, fontSize: '1.2rem', bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
                >
                  {isLoading ? 'Запуск...' : 'Запустить'}
                </Button>
              </Grid>
            </Grid>
          )}

          {tab === 'active' && (
            activeSubscriptions.length === 0 ? (
              <Typography textAlign="center" py={10} color="text.secondary">
                Нет активных автоподписок
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>ID</strong></TableCell>
                      <TableCell><strong>Аккаунтов</strong></TableCell>
                      <TableCell><strong>Каналов</strong></TableCell>
                      <TableCell><strong>Подписано</strong></TableCell>
                      <TableCell><strong>Ошибок</strong></TableCell>
                      <TableCell><strong>Статус</strong></TableCell>
                      <TableCell><strong>Прогресс</strong></TableCell>
                      <TableCell align="right"><strong>Действия</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeSubscriptions.map(s => (
                      <TableRow
                        key={s.task_id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/subscribe_to_channels/detail/${s.task_id}`)}
                      >
                        <TableCell sx={{ fontFamily: 'monospace' }}>{s.task_id.slice(0, 8)}</TableCell>
                        <TableCell>{s.accounts_count}</TableCell>
                        <TableCell>{s.channels_count}</TableCell>
                        <TableCell sx={{ color: '#10B981', fontWeight: 600 }}>{s.progress.subscribed}</TableCell>
                        <TableCell sx={{ color: s.progress.failed > 0 ? '#DC2626' : 'inherit' }}>{s.progress.failed}</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              s.status === 'running' ? 'Выполняется' :
                              s.status === 'completed' ? 'Завершена' :
                              s.status === 'stopped' ? 'Остановлена' : s.status
                            }
                            size="small"
                            color={
                              s.status === 'running' ? 'success' :
                              s.status === 'completed' ? 'success' :
                              s.status === 'stopped' ? 'warning' : 'error'
                            }
                            sx={{ minWidth: 90 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ flex: 1, bgcolor: '#e5e7eb', borderRadius: 1, height: 8 }}>
                              <Box sx={{ width: `${s.progress.percentage}%`, bgcolor: '#10B981', height: '100%', borderRadius: 1 }} />
                            </Box>
                            <span>{s.progress.percentage.toFixed(0)}%</span>
                          </Box>
                        </TableCell>
                        <TableCell align="right" onClick={e => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            {s.status === 'stopped' && (
                              <IconButton size="small" color="success" onClick={() => restoreSubscription(s.task_id)} disabled={isLoading}>
                                <PlayArrowIcon fontSize="small" />
                              </IconButton>
                            )}
                            {s.status === 'running' && (
                              <IconButton size="small" color="error" onClick={() => stopSubscription(s.task_id)} disabled={isLoading}>
                                <StopIcon fontSize="small" />
                              </IconButton>
                            )}
                            <IconButton size="small" color="error" onClick={() => deleteSubscription(s.task_id)} disabled={isLoading}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default ChannelSubscription;